import {
  computed,
  onBeforeUnmount,
  onMounted,
  watch,
  type ComputedRef,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useStore } from 'vuex';
import { useQueryClient } from '@tanstack/vue-query';
import getAccountLearnedRoutes from '@gpx-spa/queries/src/getAccountLearnedRoutes';
import { runShipmentFormComputeRoute } from '@/components/shipment/form/domain/shipment-form-compute-route';
import {
  buildLearnedRouteRequest,
  routePlanFromLearnedResponse,
} from '@/components/shipment/form/domain/learned-route-offers';
import { COMPOSER_DEBOUNCE } from '@/components/shipment/form/domain/constants';
import { hasPersistableRoutePlan } from '@/components/shipment/form/utils/shipment-form-route-composer-snapshot';
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint';
import {
  clientKeyForStopField,
  type ShipmentFormStopFieldKey,
} from '@/components/shipment/form/utils/shipment-form-stop-validation';
import { useShipmentFormAutofillSettingsStore } from '@/stores/useShipmentFormAutofillSettingsStore';
import { useShipmentFormLearnedDedupSettingsStore } from '@/stores/useShipmentFormLearnedDedupSettingsStore';
import { useShipmentFormStopTimeAutofill } from '@/composables/useShipmentFormStopTimeAutofill';
import { useShipmentFormStopsValidation } from '@/composables/useShipmentFormStopsValidation';
import { useShipmentFormAutoRoute } from '@/composables/useShipmentFormAutoRoute';
import { isShipmentFormRoutingBusy } from '@/composables/shipment-form-stops-port';
import { useShipmentFormMapScene } from '@/composables/useShipmentFormMapScene';
import type { ShipmentFormPageContext } from '@/components/shipment/form/types/shipment-form-page-context';
import { isEditPageContext } from '@/components/shipment/form/types/shipment-form-page-context';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';

export function useShipmentFormStopsSection(
  pageContext: ComputedRef<ShipmentFormPageContext | undefined>,
) {
  const vuexStore = useStore();
  const learnedRoutesAvailable = computed(
    () => vuexStore.getters['auth/learnedRoutesAvailable'],
  );

  const formStore = useShipmentFormStore();
  const {
    stops,
    routePlan,
    geometryQualityPercent,
    serverHydratedRouteFingerprint,
    serverFieldErrors,
  } = storeToRefs(formStore);

  const mapContext = computed(() => ({
    routingStrategy: pageContext.value?.routingStrategy,
    primaryTrackerPosition: pageContext.value?.primaryTrackerPosition ?? null,
    trackerPositions: pageContext.value?.trackerPositions ?? [],
    routesHydrationReady: pageContext.value?.routesHydrationReady,
    isFormHydrated: pageContext.value?.isFormHydrated,
    trackerLoading: pageContext.value?.trackerLoading,
    isMapBootstrapMode: pageContext.value?.isMapBootstrapMode,
  }));

  const scene = useShipmentFormMapScene(mapContext);

  const {
    clientStopFieldErrors,
    stopsResolver,
    validateStops,
  } = useShipmentFormStopsValidation({
    formStore,
    stops,
  });

  const autofillSettingsStore = useShipmentFormAutofillSettingsStore();
  onMounted(() => {
    void autofillSettingsStore.hydrateFromBrowserDb();
  });

  const learnedDedupSettingsStore =
    useShipmentFormLearnedDedupSettingsStore();
  const {
    dedupEnabled: learnedDedupEnabled,
    matchPercent: learnedDedupMatchPercent,
    matchThreshold: learnedDedupMatchThreshold,
  } = storeToRefs(learnedDedupSettingsStore);
  const learnedDedupSettingsReady =
    learnedDedupSettingsStore.hydrateFromBrowserDb();

  const {
    anchorConflictDialogVisible,
    conflictResolutionChoice,
    onAnchorConflictApply,
    onAnchorConflictCancel,
    suppressPlannedTimeManualMark,
    onUserPlannedTimeEdit,
  } = useShipmentFormStopTimeAutofill({
    routePlan,
    stops,
    selectedByPathId: scene.selectedByPathId,
  });

  const stopsRouteFingerprint = computed(() =>
    computeStopsRouteFingerprint(stops.value),
  );

  const queryClient = useQueryClient();
  let computeAbort: AbortController | null = null;

  async function computeRoute() {
    computeAbort?.abort();
    computeAbort = new AbortController();
    const signal = computeAbort.signal;
    await learnedDedupSettingsReady;
    if (signal.aborted) return;
    learnedDedupSettingsStore.setMaxObservedMatch(null);

    const coords = scene.routingCoordinates.value ?? [];
    const accountIdValue = scene.accountId.value ?? 0;
    const learnedBody = buildLearnedRouteRequest({
      strategy: scene.routingStrategy.value,
      stops: stops.value,
      trackerPosition: pageContext.value?.primaryTrackerPosition ?? null,
    });

    await runShipmentFormComputeRoute(
      {
        stopCoordinates: coords,
        googleEnabled: scene.hasGoogleKey.value,
        corridorBufferM: scene.corridorBufferMDebounced.value,
        learnedGoogleDedup: {
          enabled: learnedDedupEnabled.value,
          matchThreshold: learnedDedupMatchThreshold.value,
        },
        onLearnedGoogleMatchMeasured:
          learnedDedupSettingsStore.setMaxObservedMatch,
        signal,
        fetchLearnedPlan:
          learnedRoutesAvailable.value &&
          accountIdValue > 0 &&
          learnedBody
            ? async (requestSignal) => {
                const data = await getAccountLearnedRoutes.fetchQueryData(
                  queryClient,
                  accountIdValue,
                  learnedBody,
                  requestSignal,
                );
                return routePlanFromLearnedResponse(data);
              }
            : undefined,
      },
      {
        googleRouting: scene.googleRouting,
        routePlan,
        routeError: scene.routeError,
        routeWarning: scene.routeWarning,
        computingRoute: scene.computingRoute,
        routingLoading: scene.routingLoading,
        geometryQuality: geometryQualityPercent,
        geometryQualityDebounced: scene.geometryQualityDebounced,
        corridorBufferMDebounced: scene.corridorBufferMDebounced,
        commitGeometryQualityImmediately: scene.commitGeometryQualityImmediately,
        requestRouteOverview: scene.requestRouteOverview,
      },
    );

    if (signal.aborted) return;
    if (
      scene.routeError.value == null &&
      hasPersistableRoutePlan(routePlan.value)
    ) {
      formStore.markRoutePlanSyncedWithCurrentStops(stops.value);
    }
  }

  watch(
    learnedRoutesAvailable,
    () => {
      void computeRoute();
    },
    { flush: 'post' },
  );

  let learnedDedupRecomputeTimer: ReturnType<typeof setTimeout> | null = null;
  watch(
    [learnedDedupEnabled, learnedDedupMatchPercent],
    () => {
      if (!learnedDedupSettingsStore.persistenceHydrated) return;
      if (learnedDedupRecomputeTimer) {
        clearTimeout(learnedDedupRecomputeTimer);
      }
      learnedDedupRecomputeTimer = setTimeout(() => {
        learnedDedupRecomputeTimer = null;
        void computeRoute();
      }, COMPOSER_DEBOUNCE.corridorBufferMs);
    },
    { flush: 'post' },
  );
  onBeforeUnmount(() => {
    if (learnedDedupRecomputeTimer) {
      clearTimeout(learnedDedupRecomputeTimer);
    }
  });

  useShipmentFormAutoRoute({
    routesHydrationReady: computed(
      () => pageContext.value?.routesHydrationReady ?? true,
    ),
    canCallRoutingApi: scene.canCallRoutingApi,
    canCompute: scene.canCompute,
    stopsRouteFingerprint,
    serverHydratedRouteFingerprint,
    routePlan,
    formStore,
    computeRoute,
  });

  function stopFieldError(
    index: number,
    field: ShipmentFormStopFieldKey,
  ): string | null {
    const client =
      clientStopFieldErrors.value[clientKeyForStopField(index, field)];
    if (client) return client;

    const s = serverFieldErrors.value;
    if (field === 'location') {
      return (
        s[`stop_${index}`] ??
        s[`stops.${index}`] ??
        s[`stops.${index}.address`] ??
        s[`stops.${index}.lat`] ??
        null
      );
    }
    if (field === 'plannedDeparture') {
      return (
        s[`stops.${index}.planned_departure`] ??
        s[`stops.${index}.departure`] ??
        null
      );
    }
    if (field === 'plannedArrival') {
      return (
        s[`stops.${index}.planned_arrival`] ?? s[`stops.${index}.arrival`] ?? null
      );
    }
    return null;
  }

  function stopFieldErrorsFor(index: number) {
    const out: Record<string, string> = {};
    const l = stopFieldError(index, 'location');
    if (l) out.location = l;
    const d = stopFieldError(index, 'plannedDeparture');
    if (d) out.plannedDeparture = d;
    const a = stopFieldError(index, 'plannedArrival');
    if (a) out.plannedArrival = a;
    return out;
  }

  const editContext = computed(() =>
    isEditPageContext(pageContext.value) ? pageContext.value : undefined,
  );

  const stopsPort = {
    validateStops,
    selectedByPathId: scene.selectedByPathId,
    computingRoute: scene.computingRoute,
    routingLoading: scene.routingLoading,
    isRoutingBusy: () =>
      isShipmentFormRoutingBusy({
        computingRoute: scene.computingRoute.value,
        routingLoading: scene.routingLoading.value,
      }),
  };

  return {
    scene,
    stops,
    stopsResolver,
    validateStops,
    stopsPort,
    editContext,
    stopFieldErrorsFor,
    suppressPlannedTimeManualMark,
    onUserPlannedTimeEdit,
    anchorConflictDialogVisible,
    conflictResolutionChoice,
    onAnchorConflictApply,
    onAnchorConflictCancel,
    COMPOSER_DEBOUNCE,
  };
}
